import * as moment from "moment-timezone";
import { IAuthorizedHours } from "../models/AuthorizedHours.ts";
import { ReporteChequeo, ReporteRow } from "../models/ReporteChequeo.ts";
import { calcularTiempoTrabajado } from "../interfaces/formik/IReporteRelojChecador.ts";
import { formatDataForReport } from "./reporteChequeoUtils.ts";
import {
  normalizeRealChecksForDisplay,
} from "./incidentDisplayTimes.ts";
import {
  getReportDateKey,
  operationalDayKeyForVisitRow,
} from "./operationalDayReporteChequeo.ts";
import { parseTimeToMinutes } from "./payrollBranchMeta.ts";
import type { BranchReportMeta } from "./payrollBranchMeta.ts";
import {
  effectiveNightMetaForPartner,
  resolvePayrollPartnerNightScheduleEnabled,
} from "./nightScheduleEligibility.ts";
import {
  dedupePayrollVisitsByPair,
  resolvePayrollDayRegisteredHours,
  resolveProgramadoDisplayForPayrollDay,
  uniqueProgrammedSlotsFromSchedules,
  isPlannedScheduleSlot,
  recomputeScheduleHoursForChartDay,
} from "./payrollVariosDisplay.ts";
import { chartHorasExtrasExcludingDoubleShift } from "./payrollUtils.ts";
import type { PayrollVisitHourRow } from "./payrollVisitHours.ts";

export type ChartDetailedDisplayRecord = {
  fecha: string;
  entradaProgramada: string;
  salidaProgramada: string;
  entradaRegistrada: string;
  salidaRegistrada: string;
  salidaRegistradaFecha?: string;
  horasRegistradas?: number;
  horasTrabajadasEnHorario?: number;
  horasExtras?: number;
  visits?: PayrollVisitHourRow[];
};

function appendSeconds(time: string): string {
  if (!time || time === "N/A" || time === "Varios") {
    return time;
  }
  const parts = String(time).split(":");
  if (parts.length >= 3) {
    return time;
  }
  return `${time}:00`;
}

/**
 * Recalcula horas en horario siempre; extras crudas desde checadas,
 * pero respeta extras ya en 0 cuando el día ya tenía horas en horario
 * (rechazo / pendiente RH / sin incidencia aceptada).
 */
function applyRecomputedScheduleHours<T extends ChartDetailedDisplayRecord>(
  next: T,
  record: ChartDetailedDisplayRecord,
  fullDayHours: number
): T {
  const recomputed = recomputeScheduleHoursForChartDay({
    entradaRegistrada: next.entradaRegistrada,
    salidaRegistrada: next.salidaRegistrada,
    entradaProgramada: next.entradaProgramada,
    salidaProgramada: next.salidaProgramada,
    horasRegistradas: Number(next.horasRegistradas ?? record.horasRegistradas ?? 0),
    fullDayHours,
  });
  const rawExtras = chartHorasExtrasExcludingDoubleShift(
    recomputed.horasRegistradas,
    recomputed.horasExtrasRaw,
    fullDayHours
  );
  const originalExtras = Number(record.horasExtras ?? 0);
  const originalInSchedule = Number(record.horasTrabajadasEnHorario ?? 0);
  const keepZeroedExtras = originalExtras === 0 && originalInSchedule > 0;

  return {
    ...next,
    horasRegistradas: recomputed.horasRegistradas,
    horasTrabajadasEnHorario: recomputed.horasTrabajadasEnHorario,
    horasExtras: keepZeroedExtras ? 0 : rawExtras,
  };
}

/**
 * Recalcula horas en horario / extras desde checadas + programado del renglón
 * (no FKs). Útil tras snapshots con programado correcto pero horas en 0.
 */
export function recomputeChartDetailedHoursFromTimes<
  T extends ChartDetailedDisplayRecord,
>(records: T[], fullDayHours: number = 8.5): T[] {
  return (records || []).map((record) => {
    if (
      !record?.entradaRegistrada ||
      !record?.salidaRegistrada ||
      record.entradaRegistrada === "N/A" ||
      record.salidaRegistrada === "N/A"
    ) {
      return record;
    }
    if (
      !record.entradaProgramada ||
      !record.salidaProgramada ||
      record.entradaProgramada === "N/A" ||
      record.salidaProgramada === "Varios"
    ) {
      return record;
    }
    return applyRecomputedScheduleHours({ ...record }, record, fullDayHours);
  });
}

function branchMetaForRow(
  row: ReporteRow,
  payrollBranchMeta?: Record<number, BranchReportMeta>
): BranchReportMeta {
  const branchId = Number(row.branch_id ?? row.checkIn?.branch_id);
  const base =
    payrollBranchMeta?.[branchId] ?? {
      nightScheduleEnabled: false,
      cutoffMinutes: 3 * 60,
    };
  return effectiveNightMetaForPartner(
    base,
    row.checkIn?.candidate_shift_name
  );
}

/** Con nocturno: visita principal del día operativo (turno de tarde/noche, no madrugada suelta). */
function pickPrimaryNightVisitRow(rows: ReporteRow[]): ReporteRow | null {
  if (rows.length === 0) {
    return null;
  }
  if (rows.length === 1) {
    return rows[0];
  }

  const minEvening = 18 * 60;
  const scored = rows.map((row) => {
    const inTime = row.checkIn!.assistanceCheck_checkTime;
    const outTime = row.checkOut!.assistanceCheck_checkTime;
    const inMin = parseTimeToMinutes(inTime);
    const hours = calcularTiempoTrabajado(inTime, outTime);
    return {
      row,
      hours,
      inMin,
      isEvening: inMin >= minEvening,
    };
  });

  const evening = scored
    .filter((s) => s.isEvening)
    .sort((a, b) => b.hours - a.hours || b.inMin - a.inMin);
  if (evening.length > 0) {
    return evening[0].row;
  }

  return scored.sort((a, b) => b.hours - a.hours || b.inMin - a.inMin)[0].row;
}

function visitMatchesChartDay(
  row: ReporteRow,
  dateKey: string,
  realCheckRecords: ReporteChequeo[],
  payrollBranchMeta?: Record<number, BranchReportMeta>
): boolean {
  const meta = branchMetaForRow(row, payrollBranchMeta);
  if (!meta.nightScheduleEnabled) {
    return (
      getReportDateKey(row.checkIn!.assistanceCheck_checkDate) === dateKey
    );
  }
  return (
    operationalDayKeyForVisitRow(row, payrollBranchMeta, realCheckRecords) ===
    dateKey
  );
}

export type ChartDayCheckPair = {
  checkIn: { checkTime: string; checkDate?: string };
  checkOut: { checkTime: string; checkDate?: string };
};

function pickPrimaryCheckPairFromRows(
  rows: ReporteRow[],
  payrollBranchMeta?: Record<number, BranchReportMeta>
): ChartDayCheckPair | null {
  if (rows.length === 0) {
    return null;
  }

  const branchHasNight = rows.some(
    (row) => branchMetaForRow(row, payrollBranchMeta).nightScheduleEnabled
  );
  // Con varias visitas del día, preferir la de más horas (evita residuos
  // 00:xx→06:xx cuando también existe el turno real 06:xx→16:xx).
  const primary = branchHasNight
    ? pickPrimaryNightVisitRow(rows)
    : rows.length === 1
      ? rows[0]
      : rows
          .map((row) => ({
            row,
            hours: calcularTiempoTrabajado(
              row.checkIn!.assistanceCheck_checkTime,
              row.checkOut!.assistanceCheck_checkTime
            ),
          }))
          .sort((a, b) => b.hours - a.hours)[0].row;

  if (!primary?.checkIn || !primary.checkOut) {
    return null;
  }

  return {
    checkIn: {
      checkTime: primary.checkIn.assistanceCheck_checkTime,
      checkDate: primary.checkIn.assistanceCheck_checkDate
        ? String(primary.checkIn.assistanceCheck_checkDate)
        : undefined,
    },
    checkOut: {
      checkTime: primary.checkOut.assistanceCheck_checkTime,
      checkDate: primary.checkOut.assistanceCheck_checkDate
        ? String(primary.checkOut.assistanceCheck_checkDate)
        : undefined,
    },
  };
}

/** Una sola pasada sobre checadas del periodo (evita N× formatDataForReport en la tabla). */
export function buildCheckPairsByChartFecha(
  partnerId: number,
  realCheckRecords: ReporteChequeo[],
  periodStart: string,
  periodEnd: string,
  payrollBranchMeta?: Record<number, BranchReportMeta>
): Map<string, ChartDayCheckPair> {
  const byFecha = new Map<string, ChartDayCheckPair>();
  if (!realCheckRecords?.length || !partnerId) {
    return byFecha;
  }

  const rows = formatDataForReport(
    realCheckRecords,
    periodStart,
    periodEnd,
    payrollBranchMeta
  ).filter(
    (row) =>
      Number(row.checkIn?.partner_id) === partnerId &&
      row.checkIn &&
      row.checkOut
  );

  const byDateKey = new Map<string, ReporteRow[]>();
  for (const row of rows) {
    const meta = branchMetaForRow(row, payrollBranchMeta);
    const dateKey = meta.nightScheduleEnabled
      ? operationalDayKeyForVisitRow(row, payrollBranchMeta, realCheckRecords)
      : getReportDateKey(row.checkIn!.assistanceCheck_checkDate);

    if (!byDateKey.has(dateKey)) {
      byDateKey.set(dateKey, []);
    }
    byDateKey.get(dateKey)!.push(row);
  }

  for (const [dateKey, dayRows] of byDateKey) {
    const pair = pickPrimaryCheckPairFromRows(dayRows, payrollBranchMeta);
    if (!pair) continue;
    const fecha = moment.utc(dateKey, "YYYY-MM-DD").format("DD-MM-YYYY");
    byFecha.set(fecha, pair);
  }

  return byFecha;
}

function findCheckPairForChartDay(
  fecha: string,
  partnerId: number,
  realCheckRecords: ReporteChequeo[],
  periodStart: string,
  periodEnd: string,
  payrollBranchMeta?: Record<number, BranchReportMeta>
) {
  const dateKey = moment(fecha, "DD-MM-YYYY", true).isValid()
    ? moment(fecha, "DD-MM-YYYY").format("YYYY-MM-DD")
    : moment.utc(fecha).format("YYYY-MM-DD");

  const rows = formatDataForReport(
    realCheckRecords,
    periodStart,
    periodEnd,
    payrollBranchMeta
  ).filter(
    (row) =>
      Number(row.checkIn?.partner_id) === partnerId &&
      row.checkIn &&
      row.checkOut &&
      visitMatchesChartDay(row, dateKey, realCheckRecords, payrollBranchMeta)
  );

  return pickPrimaryCheckPairFromRows(rows, payrollBranchMeta);
}

export function buildChartDetailedDisplayByFecha<
  T extends ChartDetailedDisplayRecord,
>(
  records: T[],
  options: {
    /** Preferir branchId + payrollBranchMeta (sucursal + turno Nocturno del colaborador). */
    nightScheduleEnabled?: boolean;
    branchId?: number;
    authorizedSchedule?: IAuthorizedHours[];
    /** Branch schedules de nómina (schedule_startTime / schedule_exactDate). */
    payrollSchedules?: any[];
    realCheckRecords?: ReporteChequeo[];
    partnerId?: number;
    periodStart?: string;
    periodEnd?: string;
    payrollBranchMeta?: Record<number, BranchReportMeta>;
  }
): Map<string, T & { salidaRegistradaFecha?: string }> {
  // Siempre realinear con formatDataForReport (mismo pareo que el reporte de chequeo),
  // no solo en nocturno: el snapshot del chart a veces deja residuos 00:xx→06:xx.
  const checkPairsByFecha =
    options.realCheckRecords?.length &&
    options.partnerId &&
    options.periodStart &&
    options.periodEnd
      ? buildCheckPairsByChartFecha(
          options.partnerId,
          options.realCheckRecords,
          options.periodStart,
          options.periodEnd,
          options.payrollBranchMeta
        )
      : null;

  const map = new Map<string, T & { salidaRegistradaFecha?: string }>();
  for (const record of records) {
    map.set(
      record.fecha,
      applyChartDetailedNightDisplay(record, {
        ...options,
        precomputedCheckPair: checkPairsByFecha?.get(record.fecha) ?? null,
      })
    );
  }
  return map;
}

function resolveDayScheduleSources(
  fecha: string,
  authorizedSchedule?: IAuthorizedHours[],
  payrollSchedules?: any[]
): Array<{ start?: string; end?: string; startTime?: string; endTime?: string }> {
  const dateKey = moment(fecha, "DD-MM-YYYY", true).isValid()
    ? moment(fecha, "DD-MM-YYYY").format("YYYY-MM-DD")
    : moment.utc(fecha).format("YYYY-MM-DD");

  const toYmd = (raw: string) => moment.utc(raw).format("YYYY-MM-DD");

  const fromAuthorized = (authorizedSchedule || [])
    .filter((s) => s.exactDate && toYmd(s.exactDate) === dateKey)
    .map((s) => ({
      start: s.start,
      end: s.end,
      startTime: (s as any).startTime,
      endTime: (s as any).endTime,
      schedule_startTime: (s as any).schedule_startTime,
      schedule_endTime: (s as any).schedule_endTime,
    }));

  const fromPayroll = (payrollSchedules || [])
    .filter((s) => {
      const raw = s.schedule_exactDate || s.exactDate;
      return raw && toYmd(raw) === dateKey;
    })
    .map((s) => ({
      start: s.start,
      end: s.end,
      startTime: s.schedule_startTime || s.startTime,
      endTime: s.schedule_endTime || s.endTime,
      schedule_startTime: s.schedule_startTime,
      schedule_endTime: s.schedule_endTime,
    }));

  return [...fromAuthorized, ...fromPayroll];
}

export function applyChartDetailedNightDisplay<
  T extends ChartDetailedDisplayRecord,
>(
  record: T,
  options: {
    nightScheduleEnabled?: boolean;
    branchId?: number;
    authorizedSchedule?: IAuthorizedHours[];
    payrollSchedules?: any[];
    realCheckRecords?: ReporteChequeo[];
    partnerId?: number;
    periodStart?: string;
    periodEnd?: string;
    payrollBranchMeta?: Record<number, BranchReportMeta>;
    precomputedCheckPair?: ChartDayCheckPair | null;
    fullDayHours?: number;
  }
): T & { salidaRegistradaFecha?: string } {
  const partnerNightEnabled = resolvePayrollPartnerNightScheduleEnabled({
    partnerId: options.partnerId,
    branchId: options.branchId,
    realCheckRecords: options.realCheckRecords,
    payrollBranchMeta: options.payrollBranchMeta,
  });

  let next: T & { salidaRegistradaFecha?: string } = { ...record };

  if (
    record.entradaRegistrada === "N/A" &&
    record.salidaRegistrada === "N/A" &&
    options.precomputedCheckPair == null
  ) {
    return next;
  }

  const daySchedules = resolveDayScheduleSources(
    record.fecha,
    options.authorizedSchedule,
    options.payrollSchedules
  );
  const dayVisits = dedupePayrollVisitsByPair(record.visits || []);

  const pair =
    options.precomputedCheckPair !== undefined
      ? options.precomputedCheckPair
      : options.realCheckRecords?.length &&
          options.partnerId &&
          options.periodStart &&
          options.periodEnd
        ? findCheckPairForChartDay(
            record.fecha,
            options.partnerId,
            options.realCheckRecords,
            options.periodStart,
            options.periodEnd,
            options.payrollBranchMeta
          )
        : null;

  // Siempre preferir el pareo de checadas (como PaymentDetail / reporte),
  // aunque la sucursal no sea nocturna.
  if (pair?.checkIn?.checkTime && pair?.checkOut?.checkTime) {
    if (partnerNightEnabled) {
      const { checkIn, checkOut } = normalizeRealChecksForDisplay(
        pair.checkIn,
        pair.checkOut,
        true
      );
      next.entradaRegistrada =
        checkIn?.checkTime ?? pair.checkIn.checkTime;
      next.salidaRegistrada =
        checkOut?.checkTime ?? pair.checkOut.checkTime;
      if (
        checkIn?.checkDate &&
        checkOut?.checkDate &&
        String(checkIn.checkDate) !== String(checkOut.checkDate)
      ) {
        next.salidaRegistradaFecha = moment
          .utc(checkOut.checkDate)
          .format("YYYY-MM-DD");
      } else {
        delete next.salidaRegistradaFecha;
      }
    } else {
      next.entradaRegistrada = pair.checkIn.checkTime;
      next.salidaRegistrada = pair.checkOut.checkTime;
      delete next.salidaRegistradaFecha;
    }

    const hours = calcularTiempoTrabajado(
      next.entradaRegistrada,
      next.salidaRegistrada
    );
    next.horasRegistradas = hours;

    const slots = uniqueProgrammedSlotsFromSchedules(daySchedules).filter(
      (slot) => isPlannedScheduleSlot(slot.entrada, slot.salida)
    );
    const hasRealSchedule = slots.length > 0;
    const fullDayHours = options.fullDayHours ?? 8.5;

    if (!hasRealSchedule) {
      // Sin horario → programado = checada (no programado).
      next.entradaProgramada = appendSeconds(next.entradaRegistrada);
      next.salidaProgramada = appendSeconds(next.salidaRegistrada);
      next.horasTrabajadasEnHorario = hours;
      next.horasExtras = 0;
    } else if (slots.length > 1) {
      next.entradaProgramada = "Varios";
      next.salidaProgramada = "Varios";
    } else {
      next.entradaProgramada = appendSeconds(slots[0].entrada);
      next.salidaProgramada = appendSeconds(slots[0].salida);
      next = applyRecomputedScheduleHours(next, record, fullDayHours);
    }

    return next;
  }

  if (!partnerNightEnabled) {
    // Aun sin remap de checadas, corregir programado + horas si hay agenda.
    const slots = uniqueProgrammedSlotsFromSchedules(daySchedules).filter(
      (slot) => isPlannedScheduleSlot(slot.entrada, slot.salida)
    );
    const fullDayHours = options.fullDayHours ?? 8.5;
    if (slots.length === 1) {
      next.entradaProgramada = appendSeconds(slots[0].entrada);
      next.salidaProgramada = appendSeconds(slots[0].salida);
      if (
        next.entradaRegistrada &&
        next.salidaRegistrada &&
        next.entradaRegistrada !== "N/A" &&
        next.salidaRegistrada !== "N/A"
      ) {
        next = applyRecomputedScheduleHours(next, record, fullDayHours);
      }
      return next;
    }
    if (slots.length > 1) {
      next.entradaProgramada = "Varios";
      next.salidaProgramada = "Varios";
      return next;
    }
    return record;
  }

  if (
    record.entradaRegistrada === "N/A" ||
    !record.entradaRegistrada ||
    !record.salidaRegistrada ||
    record.salidaRegistrada === "N/A"
  ) {
    return next;
  }

  const rawIn = { checkTime: record.entradaRegistrada };
  const rawOut = { checkTime: record.salidaRegistrada };
  const { checkIn, checkOut } = normalizeRealChecksForDisplay(
    rawIn,
    rawOut,
    true
  );

  next.entradaRegistrada = checkIn?.checkTime ?? record.entradaRegistrada;
  next.salidaRegistrada = checkOut?.checkTime ?? record.salidaRegistrada;

  const slots = uniqueProgrammedSlotsFromSchedules(daySchedules).filter(
    (slot) => isPlannedScheduleSlot(slot.entrada, slot.salida)
  );
  const fullDayHours = options.fullDayHours ?? 8.5;
  if (slots.length === 0) {
    next.entradaProgramada = appendSeconds(next.entradaRegistrada);
    next.salidaProgramada = appendSeconds(next.salidaRegistrada);
    const horas = resolvePayrollDayRegisteredHours(dayVisits, {
      entradaRegistrada: next.entradaRegistrada,
      salidaRegistrada: next.salidaRegistrada,
      nightScheduleEnabled: partnerNightEnabled,
    });
    next.horasRegistradas = horas;
    next.horasTrabajadasEnHorario = horas;
    next.horasExtras = 0;
  } else if (slots.length > 1) {
    next.entradaProgramada = "Varios";
    next.salidaProgramada = "Varios";
  } else {
    next.entradaProgramada = appendSeconds(slots[0].entrada);
    next.salidaProgramada = appendSeconds(slots[0].salida);
    next = applyRecomputedScheduleHours(next, record, fullDayHours);
  }

  return next;
}
